import { useId, useState } from "react";

import {
  combinedIntroExistingDraft,
  type BuyerCombinedIntroEmailDto,
} from "@/api/buyers";
import { Button, Textarea, useToast } from "@/design-system/components/ui";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  useDraftCombinedIntro,
  useSendCombinedIntro,
  useUpdateCombinedIntro,
} from "@/hooks/buyers/useCombinedIntro";
import { COMBINED_INTRO_MIN_LISTINGS } from "@/lib/buyerCombinedIntroRules";
import { withListingPages } from "@/lib/listingPageUrl";

import { SendPreviewModal } from "../sendPreview/SendPreviewModal";
import { useSenderIdentity } from "../sendPreview/useSenderIdentity";
import { CombinedIntroCopyModal } from "./CombinedIntroCopyModal";
import { combinedIntroCopy as copy } from "./combinedIntroCopy";
import {
  introEligiblePicks,
  sendBlockedReasonFor,
  type CombinedIntroPick,
} from "./combinedIntroSelection";

/** How the composer opens: from the ticked rows, or on the buyer's unsent draft. */
export type CombinedIntroComposerStart =
  | { kind: "new"; picks: readonly CombinedIntroPick[] }
  | { kind: "resume"; email: BuyerCombinedIntroEmailDto };

/**
 * The composer for a COMBINED intro email — one email to one buyer introducing
 * two or more of their listings.
 *
 * <p>Two steps in one dialog. Before a draft exists it lists the ticked
 * listings and waits for "Draft the email": drafting is its OWN action, never a
 * side effect of opening, because a draft costs an AI call and replaces any
 * unsent draft the buyer already has (StrictMode would fire an on-mount draft
 * twice). After it, the draft: the literal recipient, the subject, the editable
 * message, the locked listing-page section, the numbered listings, and the four
 * actions the single intro has — Save draft, Send from Phoenix, Copy for my own
 * email, Redraft.</p>
 *
 * <p>🔴 <b>The message describes the set it was drafted for.</b> Removing a
 * listing here does not rewrite the message, so Send and Copy stay disabled —
 * with "Redraft to match the listings above." — until a redraft makes the two
 * agree again. The server refuses the same mismatch, keyed on the listings sent
 * with the request.</p>
 *
 * <p>🔴 <b>Send and Copy take the message ON SCREEN</b>, unlike the single intro
 * whose send reads the saved draft — so an unsaved edit is not a reason to
 * disable them. Save exists so a draft survives closing the dialog.</p>
 *
 * <p>Whether a send is allowed is the server's answer (`canSend`,
 * `blockedReason`), never re-derived here. The one exception is the buyer's
 * address: a missing address blocks only the Phoenix send, so it is not part of
 * `canSend`, and the sentence shown is the server's own refusal word for
 * word.</p>
 */
export function CombinedIntroComposer({
  buyerLeadId,
  buyerName,
  buyerEmail,
  start,
  openDraft,
  onClose,
  onDone,
}: {
  buyerLeadId: string;
  /** The buyer's display name — the explainer and the recipient line. */
  buyerName: string;
  buyerEmail: string | null;
  start: CombinedIntroComposerStart;
  /** The buyer's unsent draft, if any — asked about before a new draft
   *  replaces it. */
  openDraft: BuyerCombinedIntroEmailDto | null;
  onClose: () => void;
  /** A send or copy was recorded — the host clears its selection. */
  onDone: () => void;
}) {
  const toast = useToast();
  const identity = useSenderIdentity();

  const [email, setEmail] = useState<BuyerCombinedIntroEmailDto | null>(
    start.kind === "resume" ? start.email : null,
  );
  // The listings ON SCREEN, in order. Before a draft these are the ticked
  // rows; after it, the draft's listings minus any the reader removed.
  // D3a: a new email starts from the ELIGIBLE ticks only; the rest are listed
  // as "left out" with their reason and never sent to the server.
  const [listingIds, setListingIds] = useState<string[]>(() =>
    start.kind === "resume"
      ? start.email.listings.map((l) => l.submissionId)
      : introEligiblePicks(start.picks).map((p) => p.submissionId),
  );
  const [body, setBody] = useState(start.kind === "resume" ? start.email.body : "");
  const [replaceTarget, setReplaceTarget] = useState<{ draftId: string; listingCount: number } | null>(
    null,
  );
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [recordingFailed, setRecordingFailed] = useState<string | null>(null);

  const draft = useDraftCombinedIntro(buyerLeadId);
  const update = useUpdateCombinedIntro(buyerLeadId);
  const send = useSendCombinedIntro(buyerLeadId);
  // Names the reason a disabled action gives, so it is read with the control.
  const reasonId = useId();

  function adopt(dto: BuyerCombinedIntroEmailDto) {
    setEmail(dto);
    setListingIds(dto.listings.map((l) => l.submissionId));
    setBody(dto.body);
  }

  function runDraft(replaceDraftId: string | null) {
    draft.mutate(
      {
        submissionIds: listingIds,
        includeListingLinks: email?.includeListingLinks ?? false,
        replaceDraftId,
      },
      {
        onSuccess: adopt,
        onError: (e) => {
          const existing = combinedIntroExistingDraft(e);
          if (existing) setReplaceTarget(existing);
          else toast.error(e.message);
        },
      },
    );
  }

  function startDraft() {
    // A draft of our own is replaced silently — that IS a redraft. Anyone
    // else's unsent draft is asked about first (R4).
    if (email) {
      runDraft(email.id);
    } else if (openDraft) {
      setReplaceTarget({ draftId: openDraft.id, listingCount: openDraft.listings.length });
    } else {
      runDraft(null);
    }
  }

  const dirty = email != null && body.trim() !== email.body.trim();
  const closed = email?.sentAtUtc != null;

  function requestClose() {
    if (dirty && !closed) setConfirmDiscard(true);
    else onClose();
  }

  const toEmail = email ? email.buyerEmail : buyerEmail;
  const recipient = (
    <p className="text-small text-onyx m-0" data-testid="combined-intro-to">
      <span className="font-medium">{copy.to}: </span>
      {email?.buyerName ?? buyerName}{" "}
      {toEmail ? (
        <span>&lt;{toEmail}&gt;</span>
      ) : (
        <span className="text-status-warning">({copy.noEmailOnFile})</span>
      )}
    </p>
  );

  const confirmReplace = replaceTarget ? (
    <ConfirmModal
      open
      title={copy.replaceTitle}
      message={copy.replaceMessage(replaceTarget.listingCount)}
      confirmLabel={copy.replaceConfirm}
      cancelLabel={copy.replaceCancel}
      onConfirm={() => {
        const target = replaceTarget.draftId;
        setReplaceTarget(null);
        runDraft(target);
      }}
      onCancel={() => setReplaceTarget(null)}
    />
  ) : null;

  const confirmDiscardNode = confirmDiscard ? (
    <ConfirmModal
      open
      title={copy.discardTitle}
      message={copy.discardMessage}
      confirmLabel={copy.discardConfirm}
      cancelLabel={copy.discardCancel}
      destructive
      onConfirm={() => {
        setConfirmDiscard(false);
        onClose();
      }}
      onCancel={() => setConfirmDiscard(false)}
    />
  ) : null;

  // ---- Step 1: the ticked listings, before any draft ---------------------
  if (!email) {
    const picks = start.kind === "new" ? start.picks : [];
    const onScreen = listingIds
      .map((id) => picks.find((p) => p.submissionId === id))
      .filter((p): p is CombinedIntroPick => p != null);
    const leftOut = picks.filter((p) => p.leftOutReason);
    const reason = sendBlockedReasonFor(onScreen);

    return (
      <>
        <Modal
          open
          onClose={onClose}
          title={copy.composerTitle}
          size="lg"
          dismissOnBackdropClick={false}
          footer={
            <div className="flex flex-wrap items-center justify-end gap-space-3">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={startDraft}
                disabled={reason != null || draft.isPending}
                data-testid="combined-intro-draft"
              >
                {draft.isPending ? copy.drafting : copy.draftButton}
              </Button>
            </div>
          }
        >
          <div className="grid gap-space-4">
            <p className="text-small text-gray-mid m-0" data-testid="combined-intro-explainer">
              {copy.explainer(buyerName, onScreen.length)}
            </p>
            {recipient}
            <ListingList
              rows={onScreen.map((p, i) => ({
                submissionId: p.submissionId,
                number: i + 1,
                name: p.listingName,
                notes: [],
              }))}
              onRemove={
                onScreen.length > 1
                  ? (id) => setListingIds((ids) => ids.filter((x) => x !== id))
                  : undefined
              }
            />
            {leftOut.length > 0 ? (
              <div className="grid gap-space-1" data-testid="combined-intro-left-out">
                <span className="text-small font-medium">{copy.leftOutHeading}</span>
                <ul className="m-0 grid list-none gap-space-1 p-0">
                  {leftOut.map((p) => (
                    <li
                      key={p.submissionId}
                      className="text-small text-gray-mid"
                      data-testid={`combined-intro-left-out-${p.submissionId}`}
                    >
                      {p.listingName} — {copy.leftOut(p.leftOutReason ?? "")}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {reason ? (
              <p className="text-small text-status-warning m-0" data-testid="combined-intro-draft-blocked">
                {reason}
              </p>
            ) : null}
            {draft.isPending ? (
              <p role="status" className="text-small text-gray-mid m-0">
                {copy.draftingNote}
              </p>
            ) : null}
          </div>
        </Modal>
        {confirmReplace}
      </>
    );
  }

  // ---- Step 2: the draft --------------------------------------------------
  const draftedIds = email.listings.map((l) => l.submissionId);
  const setChanged =
    listingIds.length !== draftedIds.length || listingIds.some((id, i) => id !== draftedIds[i]);
  const onScreen = email.listings.filter((l) => listingIds.includes(l.submissionId));
  const empty = body.trim().length === 0;
  const busy = update.isPending || draft.isPending || send.isPending;

  // One reason, in the order a reader can act on them. Send and Copy name it
  // with `aria-describedby` whatever it is; Save and the listing-page checkbox
  // only when the message is empty, the one reason that disables them. A
  // request in flight disables them too, and says so on the button itself
  // ("Saving…", "Redrafting…").
  const actionBlocked: string | null = closed
    ? null
    : setChanged
      ? copy.redraftToMatch
      : !email.canSend
        ? email.blockedReason
        : empty
          ? copy.messageEmpty
          : null;
  const describedBy = actionBlocked ? reasonId : undefined;
  const canAct = !closed && !setChanged && email.canSend && !empty && !busy;
  const noEmail = !email.buyerEmail;

  const fullBody = withListingPages(body, email.listingPagesSection);
  const sendPayload = {
    body,
    includeListingLinks: email.includeListingLinks,
    expectedSubmissionIds: listingIds,
  };

  return (
    <>
      <Modal
        open
        onClose={requestClose}
        title={copy.composerTitle}
        size="lg"
        dismissOnBackdropClick={false}
      >
        <div className="grid gap-space-4" data-testid="combined-intro-composer">
          <p className="text-small text-gray-mid m-0" data-testid="combined-intro-explainer">
            {copy.explainer(email.buyerName, onScreen.length)}
          </p>

          {recordingFailed ? (
            <p
              role="alert"
              className="text-small text-status-error m-0"
              data-testid="combined-intro-recording-failed"
            >
              {recordingFailed}
            </p>
          ) : null}

          {recipient}

          <div className="grid gap-space-2">
            <span className="text-small font-medium">{copy.subject}</span>
            {/* Composed server-side and not editable, as the single intro's is. */}
            <p
              className="text-small text-onyx m-0 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
              data-testid="combined-intro-subject"
            >
              {email.subject}
            </p>
          </div>

          <label className="grid gap-space-2">
            <span className="text-small font-medium">{copy.message}</span>
            <Textarea
              rows={16}
              value={body}
              readOnly={closed}
              aria-label={copy.messageAriaLabel}
              data-testid="combined-intro-body"
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          {email.listingPagesSection ? (
            <div className="grid gap-space-1">
              <div
                className="rounded-md border border-border-light bg-white-warm px-space-3 py-space-2"
                data-testid="combined-intro-listing-pages"
              >
                <p className="text-small text-onyx whitespace-pre-wrap break-words m-0">
                  {email.listingPagesSection}
                </p>
              </div>
              <span className="text-caption text-gray-mid">{copy.listingPagesCaption}</span>
            </div>
          ) : null}

          <label className="flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={email.includeListingLinks}
              disabled={closed || empty || busy}
              aria-describedby={empty ? describedBy : undefined}
              data-testid="combined-intro-include-links"
              onChange={(e) =>
                update.mutate(
                  {
                    id: email.id,
                    body: { body, includeListingLinks: e.target.checked },
                  },
                  {
                    onSuccess: (dto) => setEmail(dto),
                    onError: (err) => toast.error(err.message),
                  },
                )
              }
            />
            {copy.includeLinks}
          </label>

          <div className="grid gap-space-2">
            <span className="text-small font-medium">{copy.listingsHeading}</span>
            <ListingList
              rows={onScreen.map((l) => ({
                submissionId: l.submissionId,
                number: l.number,
                name: l.listingName,
                notes: [
                  ...(l.blockedReason ? [copy.leftOut(l.blockedReason)] : []),
                  ...(email.includeListingLinks && !l.listingPageUrl ? [copy.noListingPage] : []),
                ],
              }))}
              onRemove={
                !closed && onScreen.length > 1
                  ? (id) => setListingIds((ids) => ids.filter((x) => x !== id))
                  : undefined
              }
            />
          </div>

          {actionBlocked ? (
            <p
              id={reasonId}
              className="text-small text-status-warning m-0"
              data-testid="combined-intro-blocked"
            >
              {actionBlocked}
            </p>
          ) : null}
          {closed && email.blockedReason ? (
            <p className="text-small text-gray-mid m-0" data-testid="combined-intro-closed">
              {email.blockedReason}
            </p>
          ) : null}
          {canAct && noEmail ? (
            <p className="text-small text-status-warning m-0" data-testid="combined-intro-no-email">
              {copy.noEmail}
            </p>
          ) : null}

          {closed ? (
            <div className="flex justify-end">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-space-3">
                <Button
                  variant="secondary"
                  onClick={() =>
                    update.mutate(
                      {
                        id: email.id,
                        body: { body, includeListingLinks: email.includeListingLinks },
                      },
                      {
                        onSuccess: (dto) => {
                          setEmail(dto);
                          toast.success(copy.draftSaved);
                        },
                        onError: (e) => toast.error(e.message),
                      },
                    )
                  }
                  disabled={!dirty || empty || busy}
                  aria-describedby={empty ? describedBy : undefined}
                >
                  {update.isPending ? copy.saving : copy.saveDraft}
                </Button>
                <Button
                  onClick={() => setSending(true)}
                  disabled={!canAct || noEmail}
                  aria-describedby={describedBy}
                  data-testid="combined-intro-send"
                >
                  {copy.sendFromPhoenix}
                </Button>
                {/* NOT gated on the buyer's address: the refusal of the Phoenix
                    send says "or take the copy", and disabling this would make
                    that sentence false. */}
                <Button
                  variant="secondary"
                  onClick={() => setCopying(true)}
                  disabled={!canAct}
                  aria-describedby={describedBy}
                  data-testid="combined-intro-copy"
                >
                  {copy.copyForMyOwnEmail}
                </Button>
                <Button
                  variant="text"
                  onClick={startDraft}
                  disabled={listingIds.length < COMBINED_INTRO_MIN_LISTINGS || busy}
                  data-testid="combined-intro-redraft"
                >
                  {draft.isPending ? copy.redrafting : copy.redraft}
                </Button>
              </div>
              <p className="text-caption text-gray-mid m-0">{copy.eitherPathRecords}</p>
            </>
          )}
        </div>
      </Modal>

      {sending ? (
        <SendPreviewModal
          title={copy.sendTitle}
          onClose={() => setSending(false)}
          toName={email.buyerName}
          toEmail={email.buyerEmail}
          audienceNote={copy.sendAudienceNote}
          identity={identity}
          subject={email.subject}
          body={fullBody}
          confirmLabel={copy.sendFromPhoenix}
          confirmTestId="combined-intro-send-confirm"
          pending={send.isPending}
          footnote={copy.sendFootnote(onScreen.length)}
          onConfirm={() =>
            send.mutate(
              { id: email.id, body: sendPayload },
              {
                onSuccess: (r) => {
                  setSending(false);
                  onDone();
                  if (r.recorded) {
                    toast.success(copy.sentTo(r.toEmail));
                    onClose();
                  } else {
                    // The email WENT. Say so, keep the dialog open on the closed
                    // record, and never offer the send again.
                    setEmail(r.email);
                    setRecordingFailed(r.recordingFailedMessage);
                  }
                },
                onError: (e) => toast.error(e.message),
              },
            )
          }
        />
      ) : null}

      {copying ? (
        <CombinedIntroCopyModal
          buyerLeadId={buyerLeadId}
          draftId={email.id}
          buyerName={email.buyerName}
          buyerEmail={email.buyerEmail}
          listingCount={onScreen.length}
          body={body}
          includeListingLinks={email.includeListingLinks}
          expectedSubmissionIds={listingIds}
          onCopied={(c) => {
            setEmail(c.email);
            onDone();
          }}
          onClose={() => {
            setCopying(false);
            // Once the copy is recorded the draft is closed; there is nothing
            // left to do in the composer.
            if (email.sentAtUtc != null) onClose();
          }}
        />
      ) : null}

      {confirmReplace}
      {confirmDiscardNode}
    </>
  );
}

/** The numbered listings — the same numbers the message and the listing-page
 *  section use. */
function ListingList({
  rows,
  onRemove,
}: {
  rows: { submissionId: string; number: number; name: string; notes: string[] }[];
  onRemove?: (submissionId: string) => void;
}) {
  return (
    <ol className="m-0 grid list-none gap-space-2 p-0" data-testid="combined-intro-listings">
      {rows.map((r) => (
        <li
          key={r.submissionId}
          className="flex items-start justify-between gap-space-3 rounded-md border border-border-light px-space-3 py-space-2"
          data-testid={`combined-intro-listing-${r.submissionId}`}
        >
          <div className="min-w-0">
            <p className="text-small text-onyx m-0 break-words">
              {r.number}. {r.name}
            </p>
            {r.notes.map((n) => (
              <p key={n} className="text-caption text-status-warning m-0">
                {n}
              </p>
            ))}
          </div>
          {onRemove ? (
            <Button
              variant="text"
              size="sm"
              onClick={() => onRemove(r.submissionId)}
              aria-label={copy.removeListing(r.name)}
            >
              Remove
            </Button>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
